// src/belediye/pages/reports/ReportsPage.js
import React, { useEffect, useMemo, useRef } from "react";
import { useDispatch, useSelector } from "react-redux";

import {
  initReportV1,
  clearReportV1Errors,
  clearReportV1Results,
  runReportV1Query,
  refreshReportV1Options,
  resetDraftToDefaults,
  applyDraftFilters,
  setAdvancedOpen,
  setOpenDropdown,
  selectReportV1Config,
  selectReportV1Options,
  selectReportV1Query,
  selectReportV1IsPresidentLike,
  selectReportV1AdvancedOpen,
  selectReportV1HasDraftChanges,
} from "../../redux/slices/belediyeReportV1Slice";

import PageHeader from "../../components/utils/PageHeader";
import ReportFilterBasic from "../../components/reports/filter/ReportFilterBasic";
import ReportFilterAdvanced from "../../components/reports/filter/ReportFilterAdvanced";
import ReportResults from "../../components/reports/ReportResults";
import { ReportHelpContent } from "../../components/reports/ReportHelpDrawer";

import {
  ArrowPathIcon,
  TrashIcon,
  ExclamationTriangleIcon,
  ClockIcon,
  ChevronDownIcon,
} from "@heroicons/react/24/outline";

import "../../components/reports/filter/ReportFilter.css";
import "../../components/reports/ReportResults.css";
import "../../components/reports/ReportsExport.css";
import "./ReportsPage.css";

/* =============================================================================
   ReportsPage
   - 2 ana yüzey: Filtre Paneli + Sonuç Paneli
   - CollapsibleCard yok — düz panel yapısı
   - FilterBasic kendi md-card'ından çıkarıldı (buradan wrap ediliyor)
   - Gelişmiş toggle burada yönetiliyor
============================================================================= */

export default function ReportsPage() {
  const dispatch = useDispatch();

  const configState    = useSelector(selectReportV1Config);
  const optionsState   = useSelector(selectReportV1Options);
  const queryState     = useSelector(selectReportV1Query);
  const isPresidentLike = useSelector(selectReportV1IsPresidentLike);
  const advancedOpen   = useSelector(selectReportV1AdvancedOpen);
  const hasDraftChanges = useSelector(selectReportV1HasDraftChanges);

  const configData  = configState?.data || null;
  const bootLoading = !!configState?.loading || !!optionsState?.loading || !configData;
  const bootError   = configState?.error || optionsState?.error || null;
  const queryLoading = !!queryState?.loading;
  const queryError   = queryState?.error || null;

  const bootOnceRef = useRef(false);

  const pageReady = !bootLoading && !bootError && !!configData;

  /* ---------- boot ---------- */
  useEffect(() => {
    if (bootOnceRef.current) return;
    bootOnceRef.current = true;
    dispatch(initReportV1({ autoQuery: true }));
  }, [dispatch]);

  /* ---------- actions ---------- */
  const onReset = async () => {
    dispatch(clearReportV1Errors());
    dispatch(clearReportV1Results());
    dispatch(setOpenDropdown(null));
    dispatch(setAdvancedOpen(false));
    dispatch(resetDraftToDefaults());
    dispatch(applyDraftFilters());
    await dispatch(refreshReportV1Options({}));
    await dispatch(runReportV1Query());
  };

  const onRetryBoot = async () => {
    dispatch(clearReportV1Errors());
    dispatch(clearReportV1Results());
    await dispatch(initReportV1({ autoQuery: true }));
  };

  const onRetryQuery = async () => {
    dispatch(clearReportV1Errors());
    await dispatch(runReportV1Query());
  };

  const onToggleAdvanced = () => {
    dispatch(setOpenDropdown(null));
    dispatch(setAdvancedOpen(!advancedOpen));
  };

  /* ---------- header note ---------- */
  const headerNote = useMemo(() => {
    if (!configData) return null;
    if (!isPresidentLike) return "Temel başvuru raporu görünümü";
    return "Gelişmiş filtreler ve log tabloları aktif";
  }, [configData, isPresidentLike]);

  /* ---------- bilgi içeriği (PageHeader → InfoPopover) ----------
     Eski "Yardım" modalının TÜM detay içeriği burada (ReportHelpContent). */
  const infoContent = <ReportHelpContent config={configData} />;

  /* ======================================================================= */
  return (
    <div className="applications-page rp-page">

      {/* ═══════════════════════════════════════════════════════════
          HEADER — paylaşılan PageHeader (sticky + kalın alt çizgi)
      ══════════════════════════════════════════════════════════════ */}
      <PageHeader
        title="Raporlar"
        infoTitle="Raporlar — Bilgi"
        infoContent={infoContent}
        infoWide
        tabs={
          headerNote ? <span className="rp-headnote">{headerNote}</span> : null
        }
      >
        <button
          type="button"
          className="rp-toolbtn"
          onClick={onReset}
          disabled={bootLoading || queryLoading}
          title="Sıfırla"
        >
          <TrashIcon width={18} height={18} />
          <span>Sıfırla</span>
        </button>

        <button
          type="button"
          className="rp-toolbtn"
          onClick={onRetryBoot}
          disabled={bootLoading || queryLoading}
          title="Yenile"
        >
          <ArrowPathIcon width={18} height={18} />
          <span>Yenile</span>
        </button>
      </PageHeader>

      {/* ═══════════════════════════════════════════════════════════
          BOOT LOADING
      ══════════════════════════════════════════════════════════════ */}
      {bootLoading && (
        <div className="rp-state rp-state--loading">
          <ClockIcon width={16} height={16} />
          <div>
            <div className="rp-state__title">Yükleniyor…</div>
            <div className="rp-state__sub">Config ve filtre seçenekleri hazırlanıyor.</div>
          </div>
        </div>
      )}

      {/* ═══════════════════════════════════════════════════════════
          BOOT ERROR
      ══════════════════════════════════════════════════════════════ */}
      {bootError && (
        <div className="rp-state rp-state--error">
          <ExclamationTriangleIcon width={16} height={16} />
          <div className="rp-state__content">
            <div className="rp-state__title">Başlatma Hatası</div>
            <div className="rp-state__sub" style={{ whiteSpace: "pre-wrap" }}>
              {String(bootError)}
            </div>
            <div className="rp-state__actions">
              <button
                type="button"
                className="btn-md btn-md-primary"
                onClick={onRetryBoot}
              >
                Tekrar Dene
              </button>
              <button
                type="button"
                className="btn-md btn-md-ghost"
                onClick={() => dispatch(clearReportV1Errors())}
              >
                Kapat
              </button>
            </div>
          </div>
        </div>
      )}

      {/* ═══════════════════════════════════════════════════════════
          ANA İÇERİK
      ══════════════════════════════════════════════════════════════ */}
      {pageReady && (
        <>
          {/* ─────────────────────────────────────────────────────
              FİLTRE PANELİ
          ───────────────────────────────────────────────────────── */}
          <section className="rp-panel rp-panel--filter">

            {/* Panel başlık satırı */}
            <div className="rp-panel__head">
              <div className="rp-panel__head-left">
                <span className="rp-panel__title">Filtreler</span>
                {hasDraftChanges && (
                  <span className="rp-panel__badge">Uygulanmadı</span>
                )}
              </div>

              <div className="rp-panel__head-right">
                {isPresidentLike && (
                  <button
                    type="button"
                    className={`rp-toolbtn rp-adv-toggle ${advancedOpen ? "is-open" : ""}`}
                    onClick={onToggleAdvanced}
                  >
                    <span>Gelişmiş</span>
                    <ChevronDownIcon
                      width={14}
                      height={14}
                      className={`rp-adv-toggle__chevron ${advancedOpen ? "is-open" : ""}`}
                    />
                  </button>
                )}

                <button
                  type="button"
                  className="rp-applybtn"
                  onClick={async () => {
                    dispatch(applyDraftFilters());
                    await dispatch(refreshReportV1Options({}));
                    dispatch(runReportV1Query());
                  }}
                  disabled={queryLoading}
                >
                  {queryLoading ? "Çalışıyor…" : "Uygula"}
                </button>
              </div>
            </div>

            {/* Filtreler — Basic + Gelişmiş 3. kolon ReportFilterBasic içinde yönetiliyor */}
            <div className="rp-filter-body">
              <ReportFilterBasic />
            </div>
          </section>

          {/* ─────────────────────────────────────────────────────
              SONUÇ PANELİ
          ───────────────────────────────────────────────────────── */}
          <section className="rp-panel rp-panel--results">

            {/* Query error inline banner */}
            {queryError && (
              <div className="rp-state rp-state--error rp-state--inline">
                <ExclamationTriangleIcon width={16} height={16} />
                <div className="rp-state__content">
                  <div className="rp-state__title">Sorgu Hatası</div>
                  <div className="rp-state__sub" style={{ whiteSpace: "pre-wrap" }}>
                    {String(queryError)}
                  </div>
                  <div className="rp-state__actions">
                    <button
                      type="button"
                      className="btn-md btn-md-primary"
                      onClick={onRetryQuery}
                      disabled={queryLoading}
                    >
                      Tekrar Dene
                    </button>
                    <button
                      type="button"
                      className="btn-md btn-md-ghost"
                      onClick={() => dispatch(clearReportV1Errors())}
                      disabled={queryLoading}
                    >
                      Kapat
                    </button>
                  </div>
                </div>
              </div>
            )}

            <ReportResults />
          </section>
        </>
      )}

    </div>
  );
}